/**
 * SnapshotPreviewHeader — navigation UI for snapshot preview
 *
 * Shows: metadata (timestamp, trigger), index indicator (N of M),
 * prev/next buttons, close button.
 */

import { useT } from "@magic-md/i18n/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { SnapshotMeta } from "@magic-md/engine-client/protocol";
import { cn } from "../../lib/utils";

export interface SnapshotPreviewHeaderProps {
    snapshot: SnapshotMeta;
    currentIndex: number;
    totalSnapshots: number;
    onNavigatePrev: () => void;
    onNavigateNext: () => void;
    onClose: () => void;
    canNavigatePrev: boolean;
    canNavigateNext: boolean;
    comparisonMode: "content" | "diff";
    onModeChange: (mode: "content" | "diff") => void;
}

export function SnapshotPreviewHeader({
    snapshot,
    currentIndex,
    totalSnapshots,
    onNavigatePrev,
    onNavigateNext,
    onClose,
    canNavigatePrev,
    canNavigateNext,
    comparisonMode,
    onModeChange,
}: SnapshotPreviewHeaderProps) {
    const t = useT("editor");
    const createdAt = new Date(snapshot.createdAt);
    const timeStr = createdAt.toLocaleString();
    const triggerLabel =
        snapshot.trigger === "manual"
            ? t("preview.snapshot.trigger.manual")
            : snapshot.trigger || t("preview.snapshot.trigger.auto");

    return (
        <div className="border-b border-border shrink-0">
            {/* Single metadata row with embedded segmented control */}
            <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-muted/20">
                {/* Left: scope + trigger + tag */}
                <div className="flex items-center gap-2 min-w-0">
                    {/* Scope badge */}
                    <span
                        className={cn(
                            "text-[9px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap",
                            "bg-purple-500/20 text-purple-700 dark:text-purple-400",
                        )}
                    >
                        {t("preview.snapshot.file")}
                    </span>

                    {/* Trigger label with timestamp in title */}
                    <span 
                        className="text-[10px] font-medium text-muted-foreground whitespace-nowrap"
                        title={timeStr}
                    >
                        {triggerLabel}
                    </span>

                    {/* Tag badge */}
                    {snapshot.tag && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                            {snapshot.tag}
                        </span>
                    )}
                </div>

                {/* Middle: Segmented control for content/diff mode */}
                <div className="flex items-center gap-1 rounded border border-border bg-muted/20 p-0.5 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => onModeChange("content")}
                        className={cn(
                            "text-[10px] font-medium px-2 py-1 rounded transition-colors",
                            comparisonMode === "content"
                                ? "bg-background text-foreground border border-border shadow-sm"
                                : "text-muted-foreground hover:text-foreground bg-transparent",
                        )}
                    >
                        {t("preview.snapshot.content")}
                    </button>
                    <button
                        type="button"
                        onClick={() => onModeChange("diff")}
                        className={cn(
                            "text-[10px] font-medium px-2 py-1 rounded transition-colors",
                            comparisonMode === "diff"
                                ? "bg-background text-foreground border border-border shadow-sm"
                                : "text-muted-foreground hover:text-foreground bg-transparent",
                        )}
                    >
                        {t("preview.snapshot.diff")}
                    </button>
                </div>

                {/* Right: index + navigation + close buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-[9px] text-muted-foreground/60 whitespace-nowrap">
                        {currentIndex + 1} / {totalSnapshots}
                    </span>

                    <button
                        type="button"
                        onClick={onNavigatePrev}
                        disabled={!canNavigatePrev}
                        className={cn(
                            "p-1 rounded transition-colors",
                            canNavigatePrev
                                ? "text-foreground hover:bg-muted/50 cursor-pointer"
                                : "text-muted-foreground/30 cursor-not-allowed",
                        )}
                        title={t("preview.snapshot.previous")}
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={onNavigateNext}
                        disabled={!canNavigateNext}
                        className={cn(
                            "p-1 rounded transition-colors",
                            canNavigateNext
                                ? "text-foreground hover:bg-muted/50 cursor-pointer"
                                : "text-muted-foreground/30 cursor-not-allowed",
                        )}
                        title={t("preview.snapshot.next")}
                    >
                        <ChevronRight size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        title={t("preview.snapshot.closePreview")}
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
}
